import PaginatedList from '@theme/paginated-list';

/** Blog navigation is native; topic and text searches progressively include every page. */
export default class BlogPostsList extends PaginatedList {
  #controller;
  #loading;
  #loadedPages;
  #failed = false;
  #activeFilter = 'all';
  #tools;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeSearch();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = undefined;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#tools !== this.querySelector('[data-hlf-blog-tools]')) {
      this.#controller?.abort();
      this.#controller = undefined;
      this.#initializeSearch();
    }
  }

  #initializeSearch() {
    if (this.#controller) return;
    const tools = this.querySelector('[data-hlf-blog-tools]');
    const grid = this.querySelector('[ref="grid"]');
    const search = this.querySelector('[data-hlf-blog-search]');
    const count = this.querySelector('[data-hlf-blog-count]');
    const empty = this.querySelector('[data-hlf-blog-empty]');
    const filters = Array.from(this.querySelectorAll('[data-filter]'));
    const navigation = Array.from(this.querySelectorAll('[data-hlf-blog-pagination]'));
    if (!tools || !grid || !search || !count || !empty || !filters.length) return;

    this.#tools = tools;
    this.#controller = new AbortController();
    const controller = this.#controller;
    this.#loading = undefined;
    this.#failed = false;
    this.#activeFilter = filters.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.filter || 'all';
    this.#loadedPages = new Set([Number(tools.dataset.currentPage)]);
    const pages = Number(tools.dataset.pages);

    const applyFilters = () => {
      if (controller.signal.aborted) return;
      const query = search.value.trim().toLowerCase();
      const filtering = this.#activeFilter !== 'all' || query.length > 0;
      const complete = this.#loadedPages.size >= pages;
      let visible = 0;
      for (const item of grid.querySelectorAll('.blog-post-item')) {
        const topics = (item.dataset.topics || '').split(' ');
        const categoryMatch = this.#activeFilter === 'all' || topics.includes(this.#activeFilter);
        item.hidden = !(categoryMatch && (!query || (item.dataset.search || '').includes(query)));
        if (!item.hidden) visible += 1;
      }
      grid.classList.toggle('hlf-learning-filtered', filtering || this.#loadedPages.size > 1);
      navigation.forEach((nav) => { nav.hidden = complete && pages > 1; });
      empty.hidden = visible !== 0 || (filtering && !complete);
      grid.setAttribute('aria-busy', String(Boolean(this.#loading)));
      if (filtering && !complete) {
        count.textContent = this.#failed ? tools.dataset.incomplete : tools.dataset.loading;
      } else {
        count.textContent = (visible === 1 ? count.dataset.singular : count.dataset.plural).replace('[count]', String(visible));
      }
    };

    const loadAllArticles = () => {
      if (this.#loading || this.#failed || this.#loadedPages.size >= pages) return;
      this.#loading = (async () => {
        for (let page = 1; page <= pages; page += 1) {
          if (this.#loadedPages.has(page)) continue;
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.set(tools.dataset.pageParam || 'page', String(page));
          url.searchParams.set('section_id', tools.dataset.sectionId);
          const request = new AbortController();
          const cancel = () => request.abort();
          controller.signal.addEventListener('abort', cancel, { once: true });
          const timeout = setTimeout(cancel, 20000);
          let html;
          try {
            const response = await fetch(url, { signal: request.signal });
            if (!response.ok) throw new Error('Article page request failed');
            html = await response.text();
          } finally {
            clearTimeout(timeout);
            controller.signal.removeEventListener('abort', cancel);
          }
          if (controller.signal.aborted || !this.isConnected) return;
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const pageTools = parsed.querySelector('[data-hlf-blog-tools]');
          const cards = parsed.querySelectorAll('[ref="grid"] > .blog-post-item');
          if (Number(pageTools?.dataset.currentPage) !== page || !cards.length) {
            throw new Error('Article page response is incomplete');
          }
          const ids = new Set(Array.from(grid.querySelectorAll('[data-article-id]'), (card) => card.dataset.articleId));
          for (const card of cards) {
            if (ids.has(card.dataset.articleId)) continue;
            ids.add(card.dataset.articleId);
            // Shopify numbers static block IDs within each page; imported cards need unique IDs.
            const importedIds = new Map();
            card.querySelectorAll('[id]').forEach((element) => {
              const original = element.id;
              element.id = `${original}-${card.dataset.articleId}`;
              importedIds.set(original, element.id);
            });
            card.querySelectorAll('[for], [aria-labelledby], [aria-describedby], [aria-controls], [href^="#"]').forEach((element) => {
              for (const attribute of ['for', 'aria-labelledby', 'aria-describedby', 'aria-controls']) {
                const value = element.getAttribute(attribute);
                if (value) element.setAttribute(attribute, value.split(' ').map((id) => importedIds.get(id) || id).join(' '));
              }
              const href = element.getAttribute('href');
              if (href?.startsWith('#') && importedIds.has(href.slice(1))) element.setAttribute('href', `#${importedIds.get(href.slice(1))}`);
            });
            card.style.setProperty('--col-span', '2');
            card.style.setProperty('--blog-post-card-scale', '.6');
            card.querySelectorAll('img').forEach((image) => {
              image.loading = 'lazy';
              image.setAttribute('fetchpriority', 'auto');
            });
            grid.append(card);
          }
          const sorted = Array.from(grid.querySelectorAll('.blog-post-item')).sort((a, b) => Number(a.dataset.articleOrder) - Number(b.dataset.articleOrder));
          grid.append(...sorted);
          this.#loadedPages.add(page);
          applyFilters();
        }
      })().catch(() => {
        if (!controller.signal.aborted) this.#failed = true;
      }).finally(() => {
        if (controller.signal.aborted) return;
        this.#loading = undefined;
        applyFilters();
      });
      applyFilters();
    };

    const update = () => {
      applyFilters();
      if (this.#activeFilter !== 'all' || search.value.trim()) loadAllArticles();
    };
    for (const button of filters) {
      button.addEventListener('click', () => {
        this.#activeFilter = button.dataset.filter || 'all';
        for (const filter of filters) {
          const active = filter === button;
          filter.classList.toggle('is-active', active);
          filter.setAttribute('aria-pressed', String(active));
        }
        update();
      }, { signal: controller.signal });
    }
    search.addEventListener('input', update, { signal: controller.signal });
    update();
  }
}

if (!customElements.get('blog-posts-list')) {
  customElements.define('blog-posts-list', BlogPostsList);
}
